// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Loader from "src/components/shared/loaders/Loader/Loader";
import GPAChartLegend from "./GPAChartLegend/GPAChartLegend";
import GPAChart from "./GPAChart/GPAChart";

// Types
import { GPADataAnalysisType } from "src/services/exams/types";

type ProgressionGPAChartsType = {
  isLoading: boolean;
  isError: boolean;
  isSkipped: boolean;
  eyeAnalysis: GPADataAnalysisType | null;
};

const ProgressionGPACharts = ({
  isLoading,
  isError,
  isSkipped,
  eyeAnalysis,
}: ProgressionGPAChartsType) => {
  const { t } = useTranslation();

  // Current format: one comparison-against-baseline chart per follow-up exam,
  // rendered inline within each matching exam's own row (ProgressionPatternCharts) --
  // this section only shows the shared legend/note for it.
  const visits = eyeAnalysis?.visits;
  const hasVisits = Array.isArray(visits) && visits.length > 0;

  // Legacy format: a single flat snapshot reflecting only the latest exam,
  // kept for PAs whose GPA blob was already stored before the per-exam format.
  const legacyChart = eyeAnalysis?.gpa_chart;

  const hasAnyChartData = hasVisits || Boolean(legacyChart);

  return (
    <Grid item display="flex" flexDirection="column" width="100%" gap="20px">
      <Typography fontWeight={600}>{t("GPA Charts")}</Typography>

      {isError ? (
        <>
          <Typography className="retinalogik-analysis-report" fontSize="14px">
            {t("Something went wrong, please refresh the page to try again.")}
          </Typography>
          <Typography fontSize="14px">
            {t("If the problem persists, contact support at ")}
            <b>support@retinalogik.ca</b>
          </Typography>
        </>
      ) : isLoading ? (
        <Loader
          containerProps={{
            my: "20px",
            width: "100px",
            fontSize: "14px",
            gap: "16px",
          }}
          text={t("Loading...")}
        />
      ) : isSkipped || !hasAnyChartData ? (
        <>
          <Typography className="retinalogik-analysis-report" fontSize="14px">
            {t(
              "There is not enough reliable data to generate a GPA chart. To create this chart, at least 3 exams on different days are needed, and the exams must have low FP, FN and FL."
            )}
          </Typography>
        </>
      ) : hasVisits ? (
        <>
          <GPAChartLegend />
          {eyeAnalysis?.note && (
            <Typography fontSize="14px">*{t(eyeAnalysis.note)}</Typography>
          )}
        </>
      ) : legacyChart ? (
        <>
          <GPAChartLegend />
          <GPAChart gpaPoints={legacyChart.points} />
          {legacyChart.note && (
            <Typography fontSize="14px">*{t(legacyChart.note)}</Typography>
          )}
        </>
      ) : null}
    </Grid>
  );
};

export default ProgressionGPACharts;
